import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BlogCard from '../components/BlogCard';
import { useAPI } from '../hooks/useAPI';
import APP_CONFIG from '../config/appConfig';

const Blogs = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('edit') === 'true';
  const { data: blogs, loading, error, deleteItem } = useAPI('blogs');
  const [selectedBlog, setSelectedBlog] = useState(null);


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      await deleteItem(id);
    }
  };

  const handleEdit = (blog) => {
    window.location.href = `/admin/blogs?edit=${blog.id}`;
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && selectedBlog) {
        setSelectedBlog(null);
      }
    };

    if (selectedBlog) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [selectedBlog]);

  const safeBlogs = Array.isArray(blogs) ? blogs : [];
  
  const allBlogs = safeBlogs;

  if (loading && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading blogs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="text-red-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Unable to Load Blogs</h2>
          <p className="text-gray-300 mb-4">There was an error loading the blog posts.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors border border-white"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl mb-6 script-font text-white" style={{ fontSize: 'clamp(3rem, 8vw, 5rem)' }}>
            {isEditMode ? `Edit ${t('blogs.title')}` : t('blogs.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {isEditMode 
              ? `Manage your blog posts (${allBlogs.length} total)`
              : t('blogs.subtitle')
            }
          </p>
          {isEditMode && (
            <div className="mt-4">
              <a
                href="/admin/blogs"
                className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-white"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Blog
              </a>
            </div>
          )}
        </div>

        {allBlogs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allBlogs.map((blog) => (
              <BlogCard 
                key={blog._id || blog.id} 
                blog={blog} 
                isEditMode={isEditMode}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onReadMore={setSelectedBlog}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-8">
              <svg className="w-24 h-24 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <h3 className="text-2xl font-bold text-white mb-4">No Blog Posts Yet</h3>
              <p className="text-lg text-gray-300 mb-6 max-w-md mx-auto">
                We're working on creating amazing content for you. Check back soon for inspiring stories and insights!
              </p>
              {isEditMode && (
                <a
                  href="/admin/blogs"
                  className="inline-flex items-center px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-white"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create First Blog Post
                </a>
              )}
            </div>
          </div>
        )}

        {selectedBlog && (
          <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-700">
              <div className="bg-gray-800 text-white p-6 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold mb-2">{selectedBlog.title}</h2>
                    <div className="flex items-center text-purple-100 text-sm">
                      <span>{new Date(selectedBlog.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</span>
                      <span className="mx-2">•</span>
                      <span>{selectedBlog.category}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedBlog(null)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
                {selectedBlog.image && (
                  <div className="w-full">
                    <img
                      src={selectedBlog.image.startsWith('http') ? selectedBlog.image : `${APP_CONFIG.getBaseServerUrl()}${selectedBlog.image}`}
                      alt={selectedBlog.title}
                      className="w-full h-64 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="p-8">
                  <div className="prose prose-lg max-w-none text-white leading-relaxed">
                    {selectedBlog.content ? (
                      <div className="whitespace-pre-line">{selectedBlog.content}</div>
                    ) : (
                      <div className="whitespace-pre-line">{selectedBlog.excerpt}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 px-8 py-4 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-300">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      Blog Post
                    </span>
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      {selectedBlog.content ? selectedBlog.content.split('\n').length : selectedBlog.excerpt.split('\n').length} paragraphs
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedBlog(null)}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors border border-white"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;
